'use client';

import { AnimatePresence, motion } from 'motion/react';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTotemActions, useTotemCartState } from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { useIsMounted } from '@/shared/presentation/hooks/use-is-mounted';

export function TotemFlyToCart() {
  const { flyAnimation } = useTotemCartState();
  const { clearFlyAnimation, desktopCheckoutRef, mobileCheckoutRef } = useTotemActions();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const mounted = useIsMounted();

  useEffect(() => {
    if (!flyAnimation) return;

    const isDesktop = window.matchMedia('(min-width: 640px)').matches;
    const bar = isDesktop ? desktopCheckoutRef.current : mobileCheckoutRef.current;

    if (bar) {
      setTargetRect(bar.getBoundingClientRect());
    }

    const timer = setTimeout(() => {
      clearFlyAnimation();
      setTargetRect(null);
    }, 650);

    return () => clearTimeout(timer);
  }, [flyAnimation, desktopCheckoutRef, mobileCheckoutRef, clearFlyAnimation]);

  if (!mounted || !flyAnimation || !targetRect) return null;

  const { fromRect, imageUrl } = flyAnimation;
  const startX = fromRect.left + fromRect.width / 2;
  const startY = fromRect.top + fromRect.height / 2;
  const endX = targetRect.left + targetRect.width / 2;
  const endY = targetRect.top + targetRect.height / 2;

  return createPortal(
    <AnimatePresence>
      {flyAnimation && (
        <motion.div
          key={imageUrl + startX}
          animate={{
            x: endX - startX,
            y: endY - startY,
            scale: 0.2,
            opacity: 0.6,
          }}
          className="pointer-events-none fixed z-100 size-20 overflow-hidden rounded-xl border-2 border-white shadow-lg"
          exit={{ opacity: 0 }}
          initial={{
            x: 0,
            y: 0,
            scale: 1,
            opacity: 1,
          }}
          style={{
            left: startX - 40,
            top: startY - 40,
          }}
          transition={{ duration: 0.6, ease: 'easeIn' }}
        >
          <Image alt="" className="object-cover" fill sizes="80px" src={imageUrl} />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
